import React, { useState, useEffect } from 'react';
import api from '../api';
import { 
  Radio, 
  Zap, 
  Database, 
  Clock, 
  Scale,
  Brain,
  ChevronRight,
  Activity,
  TrendingUp,
  Info,
  ChevronUp,
  ChevronDown,
  X,
  Calculator
} from 'lucide-react';

// 5D Framework dimensions with their AI methodology mappings
const DIMENSIONS = [
  {
    id: 'signals',
    name: 'Signals',
    question: 'What is happening?',
    methodology: 'Machine Learning',
    color: '#3B82F6',
    icon: Radio,
    description: 'Threat detection rate, severity distribution, and anomaly indicators across integrated sources.',
    thresholds: {
      high: { min: 70, label: 'High Activity', meaning: 'Critical detections present, elevated threat level' },
      medium: { min: 50, label: 'Normal', meaning: 'Baseline activity, routine monitoring' },
      low: { min: 0, label: 'Low', meaning: 'Minimal threat environment' }
    },
    formula: 'min(0.45 + severityWeight × 0.35 + (threats/100) × 0.2, 0.98)',
    formulaDetail: 'severityWeight = (critical × 1.0 + high × 0.7) / total'
  },
  {
    id: 'actions',
    name: 'Actions',
    question: 'What can we do?',
    methodology: 'Deep Neural Networks',
    color: '#8B5CF6',
    icon: Zap,
    description: 'Available remediation commands, coverage rate, and execution success across platforms.',
    thresholds: {
      high: { min: 70, label: 'Full Coverage', meaning: 'Automated responses ready for most threats' },
      medium: { min: 50, label: 'Partial', meaning: 'Some manual intervention needed' },
      low: { min: 0, label: 'Limited', meaning: 'Significant manual work required' }
    },
    formula: 'min(0.40 + remediationRate × 0.45 + (sources/3) × 0.15, 0.98)',
    formulaDetail: 'remediationRate = remediated / total'
  },
  {
    id: 'context',
    name: 'Context',
    question: 'Why does it matter here?',
    methodology: 'GenAI + RAG',
    color: '#14B8A6',
    icon: Database,
    description: 'Source diversity, MITRE ATT&CK mapping coverage, and knowledge base correlation.',
    thresholds: {
      high: { min: 70, label: 'Rich Context', meaning: 'Multiple sources correlated, full MITRE mapping' },
      medium: { min: 50, label: 'Basic', meaning: 'Single source, limited correlation' },
      low: { min: 0, label: 'Minimal', meaning: 'Need more integrations' }
    },
    formula: 'min(0.50 + (sources/3) × 0.30 + threatBonus, 0.98)',
    formulaDetail: 'threatBonus = threats > 10 ? 0.15 : threats × 0.015'
  },
  {
    id: 'time',
    name: 'Time',
    question: 'How is this evolving?',
    methodology: 'Temporal Agentic AI',
    color: '#F59E0B',
    icon: Clock,
    description: 'Threat velocity, active threat ratio, and campaign detection patterns.',
    thresholds: {
      high: { min: 70, label: 'HIGH URGENCY', meaning: 'Active campaign, immediate response needed' },
      medium: { min: 50, label: 'Elevated', meaning: 'Multiple active threats' },
      low: { min: 0, label: 'Normal', meaning: 'Standard threat velocity' }
    },
    formula: 'activeRatio > 0.5 ? 0.92 : activeRatio > 0.3 ? 0.78 : activeRatio > 0.1 ? 0.62 : 0.48',
    formulaDetail: 'activeRatio = activeThreats / totalThreats'
  },
  {
    id: 'judgment',
    name: 'Judgment',
    question: 'What should we do?',
    methodology: 'Agentic AI',
    color: '#EF4444',
    icon: Scale,
    description: 'Decision confidence, remediation success rate, and auto-remediation readiness.',
    thresholds: {
      high: { min: 70, label: 'OPTIMAL', meaning: 'High confidence, auto-execute recommended' },
      medium: { min: 50, label: 'Learning', meaning: 'Human approval recommended' },
      low: { min: 0, label: 'Uncertain', meaning: 'Manual review required' }
    },
    formula: 'min(0.55 + remediationRate × 0.30 + (1 - activeRatio) × 0.15, 0.98)',
    formulaDetail: 'Higher remediation success = higher confidence'
  },
];


interface AggregateData {
  totalThreats: number;
  activeThreats: number;
  remediatedThreats: number;
  sources: {
    aws: number;
    crowdstrike: number;
    okta: number;
  };
}

interface Framework5DProps {
  className?: string;
  compact?: boolean;
  onDimensionSelect?: (dimension: string) => void;
  aggregateMode?: boolean;
  aggregateData?: AggregateData;
  incidents?: any[];
  activeThreat?: {
    signalsScore?: number;
    actionsCount?: number;
    contextHits?: number;
    timeVelocity?: number;
    judgmentConfidence?: number;
  };
}

// Meter component with threshold zones
const DimensionMeter: React.FC<{
  dimension: typeof DIMENSIONS[0];
  value: number;
  detail?: string;
  liveInputs?: any;
  showCalculation?: boolean;
}> = ({ dimension, value, detail, liveInputs, showCalculation }) => {
  const percentage = Math.round(value * 100);
  const Icon = dimension.icon;
  
  // Determine zone
  const getZone = (pct: number) => {
    if (pct >= 70) return { zone: 'high', color: '#22C55E', bg: 'bg-green-500' };
    if (pct >= 50) return { zone: 'medium', color: '#F59E0B', bg: 'bg-amber-500' };
    return { zone: 'low', color: '#EF4444', bg: 'bg-red-500' };
  };
  
  const zoneInfo = getZone(percentage);
  const threshold = dimension.thresholds[zoneInfo.zone as keyof typeof dimension.thresholds];

  return (
    <div className="bg-gray-800/30 rounded-lg p-3">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${dimension.color}20` }}>
          <Icon className="w-4 h-4" style={{ color: dimension.color }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-white">{dimension.name}</span>
            <span className="text-xs text-gray-400">{dimension.question}</span>
          </div>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold" style={{ color: zoneInfo.color }}>{percentage}%</span>
        </div>
      </div>
      
      {/* Meter bar with threshold zones */}
      <div className="relative h-3 bg-gray-700 rounded-full overflow-hidden mb-2">
        {/* Zone backgrounds */}
        <div className="absolute inset-0 flex">
          <div className="w-1/2 bg-red-500/20 border-r border-gray-600" />
          <div className="w-[20%] bg-amber-500/20 border-r border-gray-600" />
          <div className="w-[30%] bg-green-500/20" />
        </div>
        {/* Threshold lines */}
        <div className="absolute left-[50%] top-0 bottom-0 w-0.5 bg-gray-500" title="50% threshold" />
        <div className="absolute left-[70%] top-0 bottom-0 w-0.5 bg-gray-500" title="70% threshold" />
        {/* Value bar */}
        <div 
          className="absolute left-0 top-0 bottom-0 rounded-full transition-all duration-500"
          style={{ 
            width: `${percentage}%`,
            backgroundColor: zoneInfo.color,
            boxShadow: `0 0 8px ${zoneInfo.color}40`
          }}
        />
        {/* Value marker */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white rounded-full shadow-lg transition-all duration-500"
          style={{ left: `calc(${percentage}% - 2px)` }}
        />
      </div>
      
      {/* Threshold labels */}
      <div className="flex justify-between text-[10px] text-gray-500 mb-2">
        <span>0%</span>
        <span className="text-red-400">|50%</span>
        <span className="text-amber-400">|70%</span>
        <span>100%</span>
      </div>
      
      {/* Status and detail */}
      <div className="flex items-center justify-between">
        <span 
          className="text-xs px-2 py-0.5 rounded-full font-medium"
          style={{ backgroundColor: `${zoneInfo.color}20`, color: zoneInfo.color }}
        >
          {threshold.label}
        </span>
        <span className="text-xs text-gray-400">{detail || threshold.meaning}</span>
      </div>
      
      {/* Live calculation (expandable) */}
      {showCalculation && liveInputs && (
        <div className="mt-2 pt-2 border-t border-gray-700">
          <div className="flex items-center gap-1 text-[10px] text-gray-500 mb-1">
            <Calculator className="w-3 h-3" />
            <span>Live Calculation:</span>
          </div>
          <code className="text-[10px] text-cyan-400 bg-gray-900/50 px-2 py-1 rounded block">
            {dimension.formula}
          </code>
          {liveInputs[dimension.id] && (
            <div className="text-[10px] text-gray-400 mt-1">
              = {liveInputs[dimension.id]}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Framework5D: React.FC<Framework5DProps> = ({ 
  className = '', 
  compact = false,
  onDimensionSelect,
  aggregateMode = false,
  aggregateData,
  incidents = [],
  activeThreat 
}) => {
  const [selectedDimension, setSelectedDimension] = useState<string | null>(null);
  const [hoveredDimension, setHoveredDimension] = useState<string | null>(null);
  const [animatedValues, setAnimatedValues] = useState<Record<string, number>>({});
  const [apiMetrics, setApiMetrics] = useState<any>(null);
  const [showLegend, setShowLegend] = useState(false);
  const [showCalculations, setShowCalculations] = useState(false);
  const [liveInputs, setLiveInputs] = useState<Record<string, string>>({});

  // Fetch 5D metrics from API
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        if (aggregateMode && incidents.length > 0) {
          const response = await api.metrics.get5D(incidents, "aggregate");
          if (response.dimensions) {
            setApiMetrics(response);
            setAnimatedValues({
              signals: response.dimensions.signals.score,
              actions: response.dimensions.actions.score,
              context: response.dimensions.context.score,
              time: response.dimensions.time.score,
              judgment: response.dimensions.judgment.score,
            });
            
            // Build live input strings for calculations
            const summary = response.summary || {};
            setLiveInputs({
              signals: `0.45 + (${summary.critical || 0}×1.0 + ${summary.high || 0}×0.7)/${summary.total || 1} × 0.35 + ${summary.total || 0}/100 × 0.2 = ${(response.dimensions.signals.score * 100).toFixed(0)}%`,
              actions: `0.40 + ${summary.remediated || 0}/${summary.total || 1} × 0.45 + ${Object.values(summary.sources || {}).filter((v: any) => v > 0).length}/3 × 0.15 = ${(response.dimensions.actions.score * 100).toFixed(0)}%`,
              context: `0.50 + ${Object.values(summary.sources || {}).filter((v: any) => v > 0).length}/3 × 0.30 + ${summary.total > 10 ? '0.15' : (summary.total || 0) + '×0.015'} = ${(response.dimensions.context.score * 100).toFixed(0)}%`,
              time: `activeRatio = ${summary.active || 0}/${summary.total || 1} = ${((summary.active || 0) / (summary.total || 1) * 100).toFixed(0)}% → ${(response.dimensions.time.score * 100).toFixed(0)}%`,
              judgment: `0.55 + ${summary.remediated || 0}/${summary.total || 1} × 0.30 + (1 - ${summary.active || 0}/${summary.total || 1}) × 0.15 = ${(response.dimensions.judgment.score * 100).toFixed(0)}%`,
            });
          }
        } else if (activeThreat) {
          const severityScore = activeThreat.signalsScore ?? 0.85;
          const actionsCount = activeThreat.actionsCount ?? 5;
          const contextHits = activeThreat.contextHits ?? 8;
          
          setAnimatedValues({
            signals: severityScore,
            actions: Math.min(0.5 + actionsCount * 0.08, 0.98),
            context: Math.min(0.55 + contextHits * 0.05, 0.98),
            time: activeThreat.timeVelocity ?? 0.65,
            judgment: activeThreat.judgmentConfidence ?? 0.94,
          });
          
          setLiveInputs({
            signals: `Severity-based: ${(severityScore * 100).toFixed(0)}%`,
            actions: `0.5 + ${actionsCount} actions × 0.08 = ${Math.min(50 + actionsCount * 8, 98)}%`,
            context: `0.55 + ${contextHits} MITRE techniques × 0.05 = ${Math.min(55 + contextHits * 5, 98)}%`,
            time: `Severity > 80%? → ${(activeThreat.timeVelocity ?? 0.65) * 100}% urgency`,
            judgment: `0.70 + severity × 0.2 = ${((activeThreat.judgmentConfidence ?? 0.94) * 100).toFixed(0)}%`,
          });
        } else if (aggregateData) {
          const total = Math.max(aggregateData.totalThreats, 1);
          const remediatedRatio = aggregateData.remediatedThreats / total;
          const activeRatio = aggregateData.activeThreats / total;
          const sourceCount = (aggregateData.sources.aws > 0 ? 1 : 0) + (aggregateData.sources.crowdstrike > 0 ? 1 : 0) + (aggregateData.sources.okta > 0 ? 1 : 0);
          
          setAnimatedValues({
            signals: Math.min(0.5 + (total / 50) * 0.5, 0.98),
            actions: Math.min(0.4 + remediatedRatio * 0.6, 0.98),
            context: Math.min(0.6 + (sourceCount / 3) * 0.35, 0.98),
            time: activeRatio > 0.3 ? 0.85 : 0.55,
            judgment: Math.min(0.7 + remediatedRatio * 0.25, 0.98),
          });
        } else {
          setAnimatedValues({ signals: 0.75, actions: 0.70, context: 0.80, time: 0.55, judgment: 0.85 });
        }
      } catch (error) {
        console.error("Failed to fetch 5D metrics:", error);
        setAnimatedValues({ signals: 0.75, actions: 0.70, context: 0.80, time: 0.55, judgment: 0.85 });
      }
    };
    fetchMetrics();
  }, [aggregateMode, aggregateData, activeThreat, incidents]);

  // Subtle animation
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedValues(prev => {
        const newValues = { ...prev };
        Object.keys(newValues).forEach(key => {
          newValues[key] = Math.max(0.3, Math.min(1, prev[key] + (Math.random() - 0.5) * 0.015));
        });
        return newValues;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleDimensionClick = (dimensionId: string) => {
    setSelectedDimension(selectedDimension === dimensionId ? null : dimensionId);
    onDimensionSelect?.(dimensionId);
  };

  const getPentagonPoint = (index: number, radius: number, centerX: number, centerY: number) => {
    const angle = (index * 72 - 90) * (Math.PI / 180);
    return {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    };
  };

  // Compact mode for incident details
  if (compact) {
    return (
      <div className={`bg-arbitium-darker border border-gray-800 rounded-xl p-4 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-arbitium-primary" />
            <h3 className="text-sm font-semibold text-white">5D AI Analysis</h3>
          </div>
          <button
            onClick={() => setShowCalculations(!showCalculations)}
            className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
          >
            <Calculator className="w-3 h-3" />
            {showCalculations ? 'Hide' : 'Show'} Math
          </button>
        </div>
        
        {/* 3-sentence summary */}
        <p className="text-xs text-gray-400 mb-3 leading-relaxed">
          The 5D Framework analyzes this threat across five dimensions. 
          <span className="text-green-400"> Green (70%+)</span> = healthy, 
          <span className="text-amber-400"> Yellow (50-70%)</span> = attention needed, 
          <span className="text-red-400"> Red (&lt;50%)</span> = action required.
        </p>

        <div className="space-y-2">
          {DIMENSIONS.map((dim) => (
            <DimensionMeter
              key={dim.id}
              dimension={dim}
              value={animatedValues[dim.id] ?? 0.5}
              detail={apiMetrics?.dimensions?.[dim.id]?.detail}
              liveInputs={liveInputs}
              showCalculation={showCalculations}
            />
          ))}
        </div>
      </div>
    );
  }

  // Full visualization with pentagon + meters
  const centerX = 175;
  const centerY = 175;
  const outerRadius = 130;
  const innerRadius = 40;

  const pentagonPoints = DIMENSIONS.map((_, i) => getPentagonPoint(i, outerRadius, centerX, centerY));
  const valuePoints = DIMENSIONS.map((dim, i) => {
    const value = animatedValues[dim.id] ?? 0.5;
    const radius = innerRadius + (outerRadius - innerRadius) * value;
    return getPentagonPoint(i, radius, centerX, centerY);
  });

  return (
    <div className={`bg-arbitium-darker border border-gray-800 rounded-xl p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-arbitium-primary/20 to-transparent rounded-lg">
            <Brain className="w-6 h-6 text-arbitium-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">5-Dimension AI Framework</h2>
            <p className="text-sm text-gray-400">Autonomous threat analysis and response</p>
          </div>
        </div>
      </div>

      {/* 3-sentence summary */}
      <div className="bg-gray-800/30 rounded-lg p-3 mb-4">
        <p className="text-sm text-gray-300 leading-relaxed">
          <strong className="text-white">The 5D Framework answers five critical questions about every threat.</strong>{' '}
          Each dimension scores 0-100% where <span className="text-green-400 font-medium">green (70%+)</span> indicates healthy/confident, {' '}
          <span className="text-amber-400 font-medium">yellow (50-70%)</span> indicates attention needed, and {' '}
          <span className="text-red-400 font-medium">red (below 50%)</span> indicates action required. {' '}
          {aggregateMode ? 'Dashboard shows aggregate analysis across all threats.' : 'Click any dimension to see detailed methodology.'}
        </p>
      </div>


      <div className="flex gap-4">
        {/* Pentagon Visualization - fixed height to match meters */}
        <div className="w-1/4 flex-shrink-0">
          <div className="h-full flex items-center justify-center">
            <svg viewBox="0 0 350 350" className="w-full h-full" style={{ maxHeight: "400px" }}>
              {/* Background grid lines */}
              {[0.25, 0.5, 0.75, 1].map((scale, i) => {
                const radius = innerRadius + (outerRadius - innerRadius) * scale;
                const points = DIMENSIONS.map((_, idx) => getPentagonPoint(idx, radius, centerX, centerY));
                return (
                  <polygon
                    key={i}
                    points={points.map(p => `${p.x},${p.y}`).join(" ")}
                    fill="none"
                    stroke={scale === 0.5 ? "#EF4444" : scale === 0.75 ? "#F59E0B" : "#374151"}
                    strokeWidth={scale === 0.5 || scale === 0.75 ? "2" : "1"}
                    strokeDasharray={i === 3 ? "0" : "4,4"}
                    opacity={scale === 0.5 || scale === 0.75 ? 0.5 : 0.3}
                  />
                );
              })}
              {/* Axis lines */}
              {pentagonPoints.map((point, i) => (
                <line key={i} x1={centerX} y1={centerY} x2={point.x} y2={point.y} stroke="#374151" strokeWidth="1" opacity={0.5} />
              ))}
              {/* Value area fill */}
              <polygon
                points={valuePoints.map(p => `${p.x},${p.y}`).join(" ")}
                fill="url(#valueGradient)"
                stroke="#6366F1"
                strokeWidth="2"
                className="transition-all duration-1000"
              />
              <defs>
                <linearGradient id="valueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.1" />
                </linearGradient>
              </defs>
              {/* Dimension points and labels */}
              {DIMENSIONS.map((dim, i) => {
                const point = pentagonPoints[i];
                const valuePoint = valuePoints[i];
                const isHovered = hoveredDimension === dim.id;
                const isSelected = selectedDimension === dim.id;
                const value = animatedValues[dim.id] ?? 0.5;
                const percentage = Math.round(value * 100);
                const zoneColor = percentage >= 70 ? "#22C55E" : percentage >= 50 ? "#F59E0B" : "#EF4444";
                const labelOffset = 25;
                const labelPoint = getPentagonPoint(i, outerRadius + labelOffset, centerX, centerY);
                return (
                  <g key={dim.id}>
                    <circle cx={point.x} cy={point.y} r={30} fill="transparent" className="cursor-pointer"
                      onMouseEnter={() => setHoveredDimension(dim.id)}
                      onMouseLeave={() => setHoveredDimension(null)}
                      onClick={() => handleDimensionClick(dim.id)}
                    />
                    <circle
                      cx={valuePoint.x} cy={valuePoint.y} r={isHovered || isSelected ? 10 : 7}
                      fill={zoneColor}
                      className="transition-all duration-300"
                      style={{ filter: isHovered || isSelected ? `drop-shadow(0 0 10px ${zoneColor})` : "none" }}
                    />
                    <text x={labelPoint.x} y={labelPoint.y - 8} textAnchor="middle" dominantBaseline="middle"
                      className={`text-xs font-medium transition-colors cursor-pointer ${isHovered || isSelected ? "fill-white" : "fill-gray-400"}`}
                      onClick={() => handleDimensionClick(dim.id)}>
                      {dim.name}
                    </text>
                    <text x={labelPoint.x} y={labelPoint.y + 8} textAnchor="middle" dominantBaseline="middle"
                      className="text-sm font-bold" style={{ fill: zoneColor }}>
                      {percentage}%
                    </text>
                  </g>
                );
              })}
              {/* Center icon */}
              <g transform={`translate(${centerX - 15}, ${centerY - 15})`}>
                <circle cx="15" cy="15" r="18" fill="#1F2937" stroke="#374151" strokeWidth="2" />
                <text x="15" y="20" textAnchor="middle" className="text-lg" fill="#6366F1">🧠</text>
              </g>
              {/* Legend for threshold lines */}
              <g transform="translate(10, 330)">
                <line x1="0" y1="0" x2="20" y2="0" stroke="#EF4444" strokeWidth="2" strokeDasharray="4,4" />
                <text x="25" y="4" className="text-[9px] fill-gray-500">50%</text>
                <line x1="70" y1="0" x2="90" y2="0" stroke="#F59E0B" strokeWidth="2" strokeDasharray="4,4" />
                <text x="95" y="4" className="text-[9px] fill-gray-500">70%</text>
              </g>
            </svg>
          </div>
        </div>

        {/* Meters + Methodology - aligned rows */}
        <div className="flex-1 space-y-2">
          {DIMENSIONS.map((dim) => {
            const Icon = dim.icon;
            const value = animatedValues[dim.id] ?? 0.5;
            const percentage = Math.round(value * 100);
            const zoneColor = percentage >= 70 ? "#22C55E" : percentage >= 50 ? "#F59E0B" : "#EF4444";
            const zoneLabel = percentage >= 70 ? "Healthy" : percentage >= 50 ? "Attention" : "Action Required";
            return (
              <div key={dim.id} className="flex gap-3">
                {/* Meter Bar */}
                <div className="flex-1 bg-gray-800/30 rounded-lg p-3">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${dim.color}20` }}>
                      <Icon className="w-4 h-4" style={{ color: dim.color }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-white">{dim.name}</span>
                        <span className="text-xs text-gray-400">{dim.question}</span>
                      </div>
                    </div>
                    <span className="text-lg font-bold" style={{ color: zoneColor }}>{percentage}%</span>
                  </div>
                  {/* Meter bar */}
                  <div className="relative h-3 bg-gray-700 rounded-full overflow-hidden">
                    <div className="absolute inset-0 flex">
                      <div className="w-1/2 bg-red-500/20 border-r border-gray-600" />
                      <div className="w-[20%] bg-amber-500/20 border-r border-gray-600" />
                      <div className="w-[30%] bg-green-500/20" />
                    </div>
                    <div className="absolute left-[50%] top-0 bottom-0 w-0.5 bg-gray-500" />
                    <div className="absolute left-[70%] top-0 bottom-0 w-0.5 bg-gray-500" />
                    <div className="absolute left-0 top-0 bottom-0 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%`, backgroundColor: zoneColor, boxShadow: `0 0 8px ${zoneColor}40` }} />
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                    <span>0%</span>
                    <span className="text-red-400">50%</span>
                    <span className="text-amber-400">70%</span>
                    <span>100%</span>
                  </div>
                </div>
                {/* Methodology */}
                <div className="w-64 flex-shrink-0 bg-gray-800/30 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${dim.color}20`, color: dim.color }}>
                      {dim.methodology}
                    </span>
                    <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${zoneColor}20`, color: zoneColor }}>
                      {zoneLabel}
                    </span>
                  </div>
                  <code className="text-[10px] text-cyan-400 block leading-relaxed truncate" title={dim.formula}>
                    {dim.formula}
                  </code>
                  {liveInputs[dim.id] && (
                    <div className="text-[10px] text-green-400 mt-1 truncate" title={liveInputs[dim.id]}>
                      = {liveInputs[dim.id]}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default Framework5D;
